import React from 'react';

export const elevatedContentItems = [
  {
    imageUrl: '/public/images/own-the-spotlight.svg',
    heading: 'Own The Spotlight',
    description:
      'Your profile remains free from competitor listings, ensuring patients keep their attention solely on your services.',
  },
  {
    imageUrl: '/public/images/ad-free-experience.svg',
    heading: 'Ad-Free Experience',
    description:
      'We remove third-party ads to ensure your profile is free from distractions.',
  },
  {
    imageUrl: '/public/images/exclusive-profile-design.svg',
    heading: 'Exclusive Profile Design',
    description:
      'Present your brand effectively through a sleek, polished design that instills confidence in your practice.',
  },
  {
    imageUrl: '/public/images/seamless-scheduling.svg',
    heading: 'Seamless Scheduling',
    description:
      'Facilitate patient bookings by providing an integrated scheduling link, enabling a direct path to your online appointment system.',
  },
];

export const healthcareProfessionalItems = [
  {
    imageUrl: '/public/images/people-group.svg',
    heading: 'Unparalleled Reach',
    description:
      'Half of all Americans who will see a doctor this year will use healthgrades.com.',
  },
  {
    imageUrl: '/public/images/chart-graph.svg',
    heading: 'High-Intent, High-Value',
    description:
      'The majority of Healthgrades visitors are key healthcare decision-makers: 70% are women, most are over 45, and more than half earn $100K+.',
  },
  {
    imageUrl: '/public/images/calendar-clock.svg',
    heading: 'Action-Oriented Audience',
    description:
      "66% of patients schedule care within a week of visiting a doctor's profile on healthgrades.com.",
  },
];

/**
 * @typedef {Object} ComparisonFeature
 * @property {string} feature - The name of the feature
 * @property {boolean} standard - Whether the feature is available in the standard profile
 * @property {boolean} elevated - Whether the feature is available in the elevated profile
 */

/**
 * Comparison table data showing which features are available for standard vs elevated profiles
 * @type {ReadonlyArray<ComparisonFeature>}
 */
export const COMPARISON_TABLE_DATA = Object.freeze([
  {
    feature: 'Update your practice details',
    standard: true,
    elevated: true,
  },
  {
    feature: 'Add your photo and a custom video',
    standard: true,
    elevated: true,
  },
  {
    feature: 'Engage with patient reviews',
    standard: true,
    elevated: true,
  },
  {
    feature: 'No competitor ads',
    standard: false,
    elevated: true,
  },
  {
    feature: 'No third-party ads',
    standard: false,
    elevated: true,
  },
  {
    feature: 'Distinctive profile design',
    standard: false,
    elevated: true,
  },
  {
    feature: 'Link to your scheduling platform',
    standard: false,
    elevated: true,
  },
]);

export const faqData = [
  {
    question: 'How long does my Elevated Profile subscription last?',
    answer:
      'Your subscription will automatically renew either monthly or annually, depending on the subscription you have chosen, until you cancel.',
  },
  {
    question: 'How do I cancel my Elevated Profile subscription?',
    answer: (
      <div>
        <p>
          You may cancel your subscription at any time in your Healthgrades Pro
          account by going to Manage Subscription page, at least 7 business days
          before your renewal date to avoid future charges. Subscription
          automatically renews until canceled. We do not offer refunds for past
          subscription months.
        </p>
        <p>
          For additional help canceling your subscription, please reach out to{' '}
          <a
            href="mailto:elevatedprofile@healthgrades.com"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            elevatedprofile@healthgrades.com
          </a>
        </p>
      </div>
    ),
  },
  {
    question:
      'When will I see Elevated Profile upgrades on the Healthgrades website?',
    answer: (
      <div>
        <p>
          Once you purchase your subscription, you will see updates to your
          profile within 1-3 business days.
        </p>
        <p>
          If you do not see updates to your profile, please reach out to{' '}
          <a
            href="mailto:elevatedprofile@healthgrades.com"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            elevatedprofile@healthgrades.com
          </a>
        </p>
      </div>
    ),
  },
  {
    question: 'Can I make changes to my Healthgrades profile?',
    answer:
      'You can make changes to your profile by logging into your Healthgrades Pro account.',
  },
  {
    question: 'How will I be billed?',
    answer: (
      <div className="space-y-4">
        <p>
          The credit or debit card you provided when you joined will be billed
          on the initial date your Elevated subscription goes into effect.
        </p>
        <p>
          If you purchased the monthly subscription, you will be billed on the
          same date of each month.
        </p>
        <p>
          If you purchased the annual subscription, you will be billed on the
          same date, one year after your original purchase date.
        </p>
        <p>
          For both monthly and annual subscriptions, you will receive an email
          notification with your billing receipt and additional information.
        </p>
      </div>
    ),
  },
  {
    question: 'How do I update my billing information?',
    answer: (
      <p>
        You can update your billing notification at any time in your
        Healthgrades Pro account by going to your Manage Subscription page. You
        will be redirected to Stripe, who we partner with for billing services,
        and you can update your billing information directly.
        <br />
        If you need assistance updating your billing information, please reach
        out to{' '}
        <a
          href="mailto:elevatedprofile@healthgrades.com"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          elevatedprofile@healthgrades.com
        </a>
      </p>
    ),
  },
  {
    question: 'How do I add a scheduling link to my profile?',
    answer: (
      <p>
        Once you purchase your subscription, log into your Healthgrades for
        Professionals account, go to My <span>Profile &gt; Appointment</span>{' '}
        Availability to add your personal scheduling link to your Healthgrades
        profile. Once you add the scheduling link, it will show on your{' '}
        <a
          href="mailto:elevatedprofile@healthgrades.com"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          Healthgrades.com
        </a>{' '}
        profile within 1-3 business days.
      </p>
    ),
  },
  {
    question: 'How do I remove ZocDoc widget from my Healthgrades profile?',
    answer: 'Contact ZocDoc directly to manage your preferences.',
  },
  {
    question: 'How do I remove Elevated Providers from my roster?',
    answer: (
      <p>
        An Elevated Profile Admin can select a Provider and choose Remove
        Subscription. This stops future renewals while the Elevated Profile
        remains active and visible on the Roster through the current paid
        billing period. After that, the Provider reverts to a Standard Profile.
        To further remove the Provider from the Roster, the provider must be
        removed separately after the Elevated Profile subscription ends.
      </p>
    ),
  },
];
